import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import { FormattedMessage } from 'react-intl'
import type { SuggestedOptionPreviewRewardPrice } from './suggestedOptionPreviewTypes'

interface Props {
  nightsText: React.ReactNode
  rewardPriceData?: SuggestedOptionPreviewRewardPrice
  needsRewardPreview: boolean
  rewardPreview?: SuggestedOptionPreviewRewardPrice
  pricingToShow?: App.Pricing
  memberPricingToShow?: App.Pricing
  convertToDisplay: (price: number) => number
  priceMode: 'total' | 'perPerson'
  rewardUnitLabel: string
  offerConfig?: App.OfferLuxPlusConfig
  showTaxesAndFees?: boolean
  taxesAndFees?: React.ReactNode
}

function SuggestedOptionPreviewPriceBlock({
  nightsText,
  rewardPriceData,
  needsRewardPreview,
  rewardPreview,
  pricingToShow,
  memberPricingToShow,
  convertToDisplay,
  priceMode,
  rewardUnitLabel,
  offerConfig,
  showTaxesAndFees,
  taxesAndFees,
}: Props) {
  const nightsCaption = <PriceRowPriceCaption>{nightsText}</PriceRowPriceCaption>

  if (rewardPriceData) {
    return <Group direction="vertical" horizontalAlign="start">
      {nightsCaption}
      <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
        <Heading variant="heading5" colour={rewardPriceData.memberCash !== undefined ? 'lux-plus' : undefined}>
          <FormattedMessage
            id="thjWZbXy5DndVyda47aB"
            defaultMessage="{points} + {cash}"
            description="Reward-seat bundle price on the suggested option preview tile on the offer page."
            values={{
              points: <FormatLoyaltyPoints points={convertToDisplay(rewardPriceData.points)} abbreviated />,
              cash: <FormatCurrency value={convertToDisplay(rewardPriceData.memberCash ?? rewardPriceData.cash)} format="roundedDollar" />,
            }}
          />
        </Heading>
        <Caption variant="medium" colour={rewardPriceData.memberCash !== undefined ? 'lux-plus' : 'neutral-three'} weight="normal">
          {rewardUnitLabel}
        </Caption>
      </Group>
      {rewardPriceData.memberCash !== undefined && (
        <Caption variant="medium" colour="neutral-two">
          <FormattedMessage
            id="FfM6_AIbUtHLt4RMQJCG"
            defaultMessage="Non-member {points} + {cash}"
            description="Non-member reward price on the suggested option preview tile."
            values={{
              points: <FormatLoyaltyPoints points={convertToDisplay(rewardPriceData.points)} abbreviated />,
              cash: <FormatCurrency value={convertToDisplay(rewardPriceData.cash)} format="roundedDollar" />,
            }}
          />
        </Caption>
      )}
    </Group>
  }

  if (needsRewardPreview && rewardPreview) {
    return <Group direction="vertical" horizontalAlign="start">
      {nightsCaption}
      <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
        <Heading variant="heading5" colour={rewardPreview.memberCash !== undefined ? 'lux-plus' : undefined}>
          <FormattedMessage
            id="9h5LgVVdVQR3VK4aBwku"
            defaultMessage="{points} + {cash}"
            description="Reward-seat preview bundle price on the suggested option preview tile showing estimated loyalty points and cash co-pay when actual reward data is unavailable."
            values={{
              points: <FormatLoyaltyPoints points={convertToDisplay(rewardPreview.points)} abbreviated />,
              cash: <FormatCurrency value={convertToDisplay(rewardPreview.memberCash ?? rewardPreview.cash)} format="roundedDollar" />,
            }}
          />
        </Heading>
        <Caption variant="medium" colour={rewardPreview.memberCash !== undefined ? 'lux-plus' : 'neutral-three'} weight="normal">
          {rewardUnitLabel}
        </Caption>
      </Group>
      {rewardPreview.memberCash !== undefined && (
        <Caption variant="medium" colour="neutral-two">
          <FormattedMessage
            id="FfM6_AIbUtHLt4RMQJCG"
            defaultMessage="Non-member {points} + {cash}"
            description="Non-member reward price on the suggested option preview tile."
            values={{
              points: <FormatLoyaltyPoints points={convertToDisplay(rewardPreview.points)} abbreviated />,
              cash: <FormatCurrency value={convertToDisplay(rewardPreview.cash)} format="roundedDollar" />,
            }}
          />
        </Caption>
      )}
    </Group>
  }

  if (!pricingToShow) return null

  return <Group direction="vertical" horizontalAlign="start">
    {nightsCaption}
    <LuxPlusPriceStack
      size="M"
      price={convertToDisplay(pricingToShow.price)}
      memberPrice={memberPricingToShow?.price && convertToDisplay(memberPricingToShow.price)}
      saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
      offerConfig={offerConfig}
      horizontalAlign="start"
    />
    {showTaxesAndFees && taxesAndFees}
  </Group>
}

export default SuggestedOptionPreviewPriceBlock
